<template>
  <div class="add-post-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          to="/audiobooks/homeview"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Add Audiobook</h1>
          <p class="text-caption text-medium-emphasis mb-0">Add an audiobook or audio drama to your collection</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex align-center flex-wrap ga-2">
        <v-btn
          variant="tonal"
          size="default"
          class="btn-modern"
          :disabled="saving || savingWishList"
          :loading="savingWishList"
          prepend-icon="mdi-bookmark-plus-outline"
          @click="saveWishList"
        >
          Save to Wishlist
        </v-btn>

        <v-btn
          color="#f43f5e"
          size="default"
          class="btn-modern text-white font-weight-bold"
          :disabled="saving || savingWishList"
          :loading="saving"
          prepend-icon="mdi-check"
          @click="saveAudiobook"
        >
          Save to Collection
        </v-btn>
      </div>
    </div>

    <!-- Alert Messages -->
    <v-alert v-if="error" type="error" class="mb-4" closable variant="tonal" @click:close="error = ''">
      {{ error }}
    </v-alert>

    <v-alert v-if="autofillMessage" type="info" class="mb-4" closable variant="tonal" @click:close="autofillMessage = ''">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-information-outline</v-icon>
        {{ autofillMessage }}
      </div>
    </v-alert>

    <!-- Google Books Smart Autofill Search Bar -->
    <v-card class="glass-card pa-4 mb-6 autofill-card">
      <div class="d-flex align-center ga-2 mb-2">
        <v-icon color="#f43f5e" size="20">mdi-auto-fix</v-icon>
        <span class="text-subtitle-2 font-weight-bold">Google Books Auto-Fill</span>
      </div>
      <v-row dense align="center">
        <v-col cols="12" sm="5">
          <v-text-field
            v-model="audiobook.title"
            placeholder="Audiobook title (e.g. Harry Potter, Project Hail Mary)..."
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            @keyup.enter="autofill"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="audiobook.author"
            placeholder="Author (optional)..."
            density="compact"
            hide-details
            prepend-inner-icon="mdi-account-edit"
            variant="outlined"
            @keyup.enter="autofill"
          />
        </v-col>
        <v-col cols="12" sm="3" class="d-flex ga-2">
          <v-btn
            color="#f43f5e"
            variant="flat"
            block
            class="btn-modern text-white font-weight-bold"
            :loading="searchingBooks"
            :disabled="!audiobook.title"
            prepend-icon="mdi-sparkles"
            @click="autofill"
          >
            Auto-Fill
          </v-btn>
        </v-col>
      </v-row>

      <!-- Matches Switcher -->
      <div v-if="searchResults.length > 1" class="mt-3 pt-3 border-t-subtle d-flex align-center justify-space-between flex-wrap ga-2">
        <span class="text-caption text-medium-emphasis">
          Match {{ audiobookInstance + 1 }} of {{ searchResults.length }}: <strong>{{ searchResults[audiobookInstance]?.volumeInfo?.title }}</strong>
        </span>
        <v-btn
          size="x-small"
          variant="tonal"
          class="btn-modern"
          prepend-icon="mdi-skip-next"
          @click="autofillNext"
        >
          Try Next Match
        </v-btn>
      </div>
    </v-card>

    <!-- Main Form Grid -->
    <v-form ref="formRef">
      <v-row>
        <!-- Left Column: Cover Preview -->
        <v-col cols="12" md="4" lg="3">
          <v-card class="glass-card pa-4 text-center sticky-poster-card">
            <div class="text-subtitle-2 font-weight-bold mb-3 text-left">Audiobook Cover</div>

            <div class="poster-dropzone mb-3">
              <v-img
                v-if="posterPreview"
                :src="posterPreview"
                :aspect-ratio="2 / 3"
                cover
                class="rounded-lg shadow-md"
              />
              <div v-else class="poster-empty-placeholder">
                <v-icon size="48" color="#f43f5e" class="mb-2">mdi-headphones</v-icon>
                <div class="text-caption font-weight-bold">No Cover Selected</div>
                <div class="text-caption text-medium-emphasis">Upload an image or use auto-fill</div>
              </div>
            </div>

            <v-file-input
              v-model="imageFile"
              label="Upload Custom Cover"
              accept="image/*"
              density="compact"
              hide-details
              prepend-icon=""
              prepend-inner-icon="mdi-upload"
              variant="outlined"
              class="mb-2"
            />

            <v-text-field
              v-model="audiobook.imageurl"
              label="Or Cover Image URL"
              placeholder="https://..."
              density="compact"
              hide-details
              variant="outlined"
            />
          </v-card>
        </v-col>

        <!-- Right Column: Form Sections -->
        <v-col cols="12" md="8" lg="9">
          <!-- Section 1: Details -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="#f43f5e" size="22">mdi-information-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Audiobook Details</h3>
            </div>

            <v-row dense>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="audiobook.title"
                  label="Audiobook Title *"
                  density="comfortable"
                  :rules="[required]"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="audiobook.category"
                  :items="CATEGORIES"
                  label="Category *"
                  density="comfortable"
                  :rules="[required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="audiobook.author"
                  label="Author / Writer"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="audiobook.narrator"
                  label="Narrator / Performer"
                  density="comfortable"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="audiobook.original"
                  label="Original Title (Optional)"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="audiobook.duration"
                  label="Runtime (e.g. 10h 30m)"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="audiobook.year"
                  label="Release Year"
                  density="comfortable"
                />
              </v-col>

              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="audiobook.publishedDate"
                  label="Published Date"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-select
                  v-model="audiobook.language"
                  :items="LANGUAGES"
                  label="Language"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="audiobook.type"
                  label="Format / Audio Type"
                  density="comfortable"
                />
              </v-col>

              <!-- Star Rating -->
              <v-col cols="12" sm="9" class="d-flex align-center ga-3 mt-2">
                <span class="text-caption font-weight-bold text-medium-emphasis">Your Rating:</span>
                <v-rating
                  v-model="ratingNumber"
                  half-increments
                  density="comfortable"
                  color="amber"
                  active-color="amber"
                />
                <span class="text-body-2 font-weight-bold">{{ ratingNumber }} / 5</span>
              </v-col>
            </v-row>
          </v-card>

          <!-- Section 2: Description -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="#f43f5e" size="22">mdi-text-box-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Summary & Description</h3>
            </div>

            <v-textarea
              v-model="audiobook.description"
              label="Audiobook Description / Synopsis"
              rows="5"
              auto-grow
              density="comfortable"
              variant="outlined"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import axios from 'axios'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { BOOKS_API_KEY, booksApiUrl } from '@/config/googlebooks'
import { CATEGORIES, LANGUAGES, QUALITIES } from '@/config/audiobooks'
import AudiobooksAPI from '@/services/audiobooks'
import { appendAudiobookFormData, defaultAudiobookForm } from '@/utils/audiobookForm'
import { apiErrorMessage } from '@/utils/formValidate'

export default {
  name: 'AddAudioBookPost',
  setup() {
    const router = useRouter()
    const formRef = ref(null)
    const audiobook = reactive(defaultAudiobookForm())
    const imageFile = ref(null)
    const saving = ref(false)
    const savingWishList = ref(false)
    const searchingBooks = ref(false)
    const error = ref('')
    const autofillMessage = ref('')
    const audiobookInstance = ref(0)
    const searchResults = ref([])

    const required = (value) => !!value || 'This field is required'

    const ratingNumber = computed({
      get: () => Number(audiobook.rating) || 0,
      set: (value) => {
        audiobook.rating = String(value)
      },
    })

    const posterPreview = computed(() => {
      if (audiobook.imageurl) return audiobook.imageurl
      const file = imageFile.value
      if (!file) return ''
      const selected = Array.isArray(file) ? file[0] : file
      return selected ? URL.createObjectURL(selected) : ''
    })

    const buildQuery = () => {
      const parts = []
      const title = String(audiobook.title || '').trim()
      const author = String(audiobook.author || '').trim()
      if (title) parts.push(`intitle:${title}`)
      if (author) parts.push(`inauthor:${author}`)
      return parts.join(' ')
    }

    const autofill = async () => {
      autofillMessage.value = ''
      error.value = ''
      if (!audiobook.title || !audiobook.title.trim()) return

      if (!BOOKS_API_KEY) {
        autofillMessage.value = 'Google Books API key not configured. You can manually enter details.'
        return
      }

      searchingBooks.value = true
      try {
        if (!searchResults.value.length) {
          const { data } = await axios.get(booksApiUrl, {
            params: { key: BOOKS_API_KEY, q: buildQuery(), langRestrict: 'fr' },
          })
          searchResults.value = data.items || []
          audiobookInstance.value = 0
        }
        if (!searchResults.value.length) {
          autofillMessage.value = 'No Google Books results found for this title.'
          return
        }
        applyGoogleBooksResult(searchResults.value[audiobookInstance.value])
      } catch (err) {
        error.value = err.message || 'Google Audiobooks autofill failed'
      } finally {
        searchingBooks.value = false
      }
    }

    const autofillNext = () => {
      if (!searchResults.value.length) {
        autofill()
        return
      }
      audiobookInstance.value = (audiobookInstance.value + 1) % searchResults.value.length
      applyGoogleBooksResult(searchResults.value[audiobookInstance.value])
    }

    const applyGoogleBooksResult = (result) => {
      if (!result) return
      const info = result.volumeInfo || {}
      if (info.title) audiobook.title = info.title
      const publishedDate = (info.publishedDate || '').substring(0, 10)
      audiobook.year = publishedDate.substring(0, 4)
      audiobook.publishedDate = publishedDate
      audiobook.pageCount = info.pageCount != null ? String(info.pageCount) : ''
      audiobook.type = info.printType || ''
      const thumbnail = info.imageLinks?.thumbnail || info.imageLinks?.smallThumbnail || ''
      audiobook.imageurl = thumbnail.replace(/^http:/, 'https:')
      audiobook.description = info.description || ''
      audiobook.author = (info.authors || []).join(', ')

      autofillMessage.value = `Loaded: "${info.title || 'result'}" (${audiobook.year || 'N/A'})`
    }

    const savePost = async (markedValue = '') => {
      error.value = ''
      const title = String(audiobook.title || '').trim()
      const category = String(audiobook.category || '').trim()

      if (!title || !category) {
        error.value = 'Title and category are required before saving.'
        return
      }

      audiobook.title = title
      audiobook.category = category
      audiobook.marked = markedValue

      const isWishList = markedValue === '2'
      if (isWishList) {
        savingWishList.value = true
      } else {
        saving.value = true
      }

      try {
        const formData = new FormData()
        appendAudiobookFormData(formData, audiobook)
        if (imageFile.value) {
          const file = Array.isArray(imageFile.value) ? imageFile.value[0] : imageFile.value
          if (file) formData.append('image', file)
        }
        await AudiobooksAPI.addPost(formData)
        const message = isWishList ? 'Audiobook added to wish list.' : 'Audiobook added to collection.'
        router.push({ name: 'AudiobooksHome', query: { message } })
      } catch (err) {
        error.value = apiErrorMessage(err, 'Failed to save audiobook')
      } finally {
        saving.value = false
        savingWishList.value = false
      }
    }

    const saveAudiobook = () => savePost('0')
    const saveWishList = () => savePost('2')

    return {
      formRef,
      audiobook,
      imageFile,
      saving,
      savingWishList,
      searchingBooks,
      error,
      autofillMessage,
      searchResults,
      audiobookInstance,
      CATEGORIES,
      LANGUAGES,
      QUALITIES,
      required,
      ratingNumber,
      posterPreview,
      autofill,
      autofillNext,
      saveAudiobook,
      saveWishList,
    }
  },
}
</script>

<style scoped>
.add-post-view {
  max-width: 1240px;
  margin: 0 auto;
}

.autofill-card {
  border: 1px solid rgba(244, 63, 94, 0.3) !important;
  background: linear-gradient(135deg, rgba(244, 63, 94, 0.06), rgba(251, 113, 133, 0.04)) !important;
}

.sticky-poster-card {
  position: sticky;
  top: 80px;
}

.poster-dropzone {
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 12px;
  overflow: hidden;
}

.poster-empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed rgba(128, 128, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.05);
}

.border-t-subtle {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}
</style>

